import React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../../../components/Modal';
import { Button } from '../../../../components/Button';
import { Select } from '../../../../components/Select';
import { Field } from '../../../../components/Field';
import { TextArea } from '../../../../components/TextArea';
import type { BlogCategory } from '../../../../mock-data/types';
import { CATEGORY_NAME_MAX, slugify } from '../../../blog/slug';
import { Copy, Fields } from './CategoryPrompt.styles';

/**
 * Sentinel option value for "create a new category". Same device as the
 * editor's translation-group picker: a native `<select>` can only hold strings,
 * so the one choice that is not a category is a value no category id can be.
 */
const NEW_CATEGORY = '__new__';

/** What the owner chose: a category that exists, or one to make first. */
export type CategorySelection =
  | { kind: 'existing'; id: string }
  | { kind: 'new'; name: string; slug: string; description: string };

export type CategoryPromptProps = {
  open: boolean;
  categories: BlogCategory[];
  /**
   * The action the owner pressed, offered again here once a category is chosen:
   * "Save draft", "Save changes" or "Publish". Translate at the call site.
   */
  actionLabel: string;
  /** The page is writing; the action waits rather than firing twice. */
  busy?: boolean;
  /**
   * Whether the category list has been read. Until then the picker waits
   * rather than claiming the brand has no categories: on a fresh new-article
   * page the form is usable before the list has landed.
   */
  loaded?: boolean;
  onClose: () => void;
  onConfirm: (selection: CategorySelection) => void;
};

/**
 * Stops a save or a publish on an article with no category, and fixes that
 * without leaving the dialog.
 *
 * Every article belongs to a category: it is the first segment of the public
 * URL and the cluster the article is read in, so a save without one is a save
 * the site cannot place. Rather than a red field and a trip to the list page,
 * the owner picks one of the existing categories here, or creates one on the
 * spot through the same Name / Slug / Description fields the list page's New
 * category dialog has. With no categories at all, the dialog opens straight on
 * those fields.
 *
 * Nothing here writes: the choice is handed back to the page, which creates the
 * category if needed and then finishes the action that was pressed. The fields
 * reset every time the dialog OPENS, so a cancelled attempt does not leak into
 * the next one.
 */
export const CategoryPrompt = ({
  open,
  categories,
  actionLabel,
  busy = false,
  loaded = true,
  onClose,
  onConfirm,
}: CategoryPromptProps): React.ReactElement => {
  const { t } = useTranslation();
  const [picked, setPicked] = React.useState('');
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setPicked('');
    setName('');
    setDescription('');
  }, [open]);

  const none = loaded && categories.length === 0;
  const creating = none || picked === NEW_CATEGORY;
  // The slug is never typed: it follows the name, and the server generates the
  // authoritative one anyway (see the list page's saveCategory).
  const slug = slugify(name);
  const ready = creating ? name.trim() !== '' : picked !== '';

  const confirm = (): void => {
    if (!ready) return;
    if (creating) onConfirm({ kind: 'new', name: name.trim(), slug, description });
    else onConfirm({ kind: 'existing', id: picked });
  };

  const options = [
    ...categories.map((category) => ({ value: category.id, label: category.name })),
    { value: NEW_CATEGORY, label: t('Create a new category') },
  ];

  return (
    <Modal
      open={open}
      title={t('Assign a category')}
      onClose={onClose}
      footer={
        <>
          <Button label={t('Cancel')} variant="outline" onClick={onClose} />
          <Button label={actionLabel} disabled={!ready || busy || !loaded} onClick={confirm} />
        </>
      }
    >
      {none ? (
        <Copy
          text="Every article needs a category. It is the first part of the article's public URL and the group it is read in on your casino blog. There are no categories yet, so create the first one here."
          tag="paragraph"
        />
      ) : (
        <Copy
          text="Every article needs a category. It is the first part of the article's public URL and the group it is read in on your casino blog. Pick one, or create a new one here."
          tag="paragraph"
        />
      )}
      <Fields>
        {none ? null : (
          <Select
            label={t('Category')}
            value={picked}
            disabled={busy || !loaded}
            onChange={setPicked}
            options={options}
            placeholder={t('Pick a category')}
          />
        )}
        {creating ? (
          <>
            <Field label={t('Name')} maxLength={CATEGORY_NAME_MAX} value={name} disabled={busy} onChange={setName} />
            {/* Read-only: the slug is auto-generated from the name. */}
            <Field label={t('Slug')} value={slug} disabled onChange={() => undefined} />
            <TextArea
              label={t('Description')}
              rows={3}
              value={description}
              disabled={busy}
              onChange={setDescription}
              placeholder={t('Shown as the intro of the public category page')}
            />
          </>
        ) : null}
      </Fields>
    </Modal>
  );
};

export default CategoryPrompt;
